<template>
  <div class="d-flex flex-column">
    <div class="mb-3">
      <div class="dx-field-item-label-text mb-1">{{ $t("retrocomisiones.grupo.selector") }}</div>
      <div class="d-flex flex-row gap-2">
        <DxSelectBox v-if="gruposSelect.length" v-model="idGrupoSeleccionado" :data-source="gruposSelect" display-expr="Texto" value-expr="Id"
        @value-changed="onGrupoChanged" :width="270"  />
        <div class="grid-header p-0">
        <div class="toolbar">
          <button type="button" class="btn btn-link" :title="$t('retrocomisiones.grupo.nuevo')" @click.prevent="crearGrupo">
            <i class="fa-solid fa-plus"></i>
          </button>
        </div>
        </div>
      </div>
    </div>

    <div class="grid-header">
      <h1>{{ $t("retrocomisiones.grid.titulo_oferta") }}</h1>
      <div class="toolbar">
        <button type="button" :disabled="!idGrupoSeleccionado" @click.prevent="crearRetroComision(TipoConceptoCriterioRetrocomisionEnergiaEnum.Oferta)"
          class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>

    <DxDataGrid v-bind="gridProperties" :data-source="criteriosOferta" ref="grid">
      <DxColumnFixing :enabled="true" />

      <DxColumn data-field="Importe" :caption="$t('retrocomisiones.grid.importe')" data-type="number" cell-template="importeTemplate">
      </DxColumn>
      <DxColumn data-field="PeriodoHasta" :caption="$t('retrocomisiones.grid.periodo_hasta')" data-type="number">
      </DxColumn>
      <DxColumn data-field="TarifaAcceso" :caption="$t('retrocomisiones.grid.tarifa_acceso')" :calculated-cell-value="calculateTarifaAcceso" data-type="string">
      </DxColumn>
      <DxColumn data-field="AplicaPersonaFisica" :caption="$t('retrocomisiones.grid.aplica_fisica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="AplicaPersonaJuridica" :caption="$t('retrocomisiones.grid.aplica_juridica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('retrocomisiones.grid.acciones')" :fixed="true" fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('retrocomisiones.grid.modificar'),
          icon: 'fa-regular fa-edit text-success',
          onClick: (e: any) => verDetalle(e.row?.data.Id, TipoConceptoCriterioRetrocomisionEnergiaEnum.Oferta)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('retrocomisiones.grid.borrar'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => borrar(e.row?.data.Id)
        }
      ]" />

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #importeTemplate="{ data }">
        <span>{{ data.value }} %</span>
      </template>
    </DxDataGrid>

    <div class="grid-header">
      <h1>{{ $t("retrocomisiones.grid.titulo_servicio") }}</h1>
      <div class="toolbar">
        <button type="button" :disabled="!idGrupoSeleccionado" @click.prevent="crearRetroComision(TipoConceptoCriterioRetrocomisionEnergiaEnum.Servicio)"
          class="btn btn-link">
          <i class="fa-solid fa-plus"></i>
        </button>
      </div>
    </div>
    <DxDataGrid v-bind="gridProperties" :data-source="criteriosServicio" ref="grid">
      <DxColumnFixing :enabled="true" />

      <DxColumn data-field="Importe" :caption="$t('retrocomisiones.grid.importe')" data-type="number" cell-template="importeTemplate">
      </DxColumn>
      <DxColumn data-field="PeriodoHasta" :caption="$t('retrocomisiones.grid.periodo_hasta')" data-type="number">
      </DxColumn>
      <DxColumn data-field="TarifaAcceso" :caption="$t('retrocomisiones.grid.tarifa_acceso')" :calculated-cell-value="calculateTarifaAcceso" data-type="string">
      </DxColumn>
      <DxColumn data-field="AplicaPersonaFisica" :caption="$t('retrocomisiones.grid.aplica_fisica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn data-field="AplicaPersonaJuridica" :caption="$t('retrocomisiones.grid.aplica_juridica')" cell-template="booleanTemplate">
        <DxLookup v-bind="getColumnLookupBoolean()"></DxLookup>
      </DxColumn>
      <DxColumn type="buttons" :caption="$t('retrocomisiones.grid.acciones')" :fixed="true" fixed-position="right" :buttons="[
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('retrocomisiones.grid.modificar'),
          icon: 'fa-regular fa-edit text-success',
          onClick: (e: any) => verDetalle(e.row?.data.Id, TipoConceptoCriterioRetrocomisionEnergiaEnum.Servicio)
        },
        {
          cssClass: 'btn btn-link text-decoration-none btn-sm',
          text: $t('retrocomisiones.grid.borrar'),
          icon: 'fa-regular fa-trash text-success',
          onClick: (e: any) => borrar(e.row?.data.Id)
        }
      ]" />

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>
      <template #importeTemplate="{ data }">
        <span>{{ data.value }} %</span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { DxDataGrid, DxColumn, DxLookup, DxColumnFixing } from "devextreme-vue/data-grid";
import { DxSelectBox } from "devextreme-vue";
import { getDefaultGridProperties, getColumnLookupBoolean, } from "@helpers/grid-helper";
import { useApi } from "@api/integration";
import { useIntl } from "@services/i18n";
import {
  TipoConceptoCriterioRetrocomisionEnergiaEnum,
  type DetailCriterioRetrocomisionEnergiaModel,
  type SelectBoxGuidEntityWithDefaultModel
} from "@api/api";
import { useModalService } from "@services/modal-service";
import { messageBoxResult } from "@components/dialogs/message-box";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import BooleanTemplate from "@/.shared/components/BooleanTemplate.vue";
import RetrocomisionEnergiaForm from "@/components/retrocomisiones-energia/RetrocomisionEnergiaForm.vue";
import GrupoCriterioRetrocomisionEnergiaForm from "@/components/retrocomisiones-energia/GrupoCriterioRetrocomisionEnergiaForm.vue";
import { CreateRetrocomisionEnergia } from "@/components/retrocomisiones-energia/models/CreateRetrocomisionEnergia";
import { resolveGrupoPorDefecto } from "@/components/retrocomisiones-energia/helpers/resolveGrupoPorDefecto";

type GrupoSelectItem = SelectBoxGuidEntityWithDefaultModel & { Texto: string };

const intl = useIntl();
const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<DetailCriterioRetrocomisionEnergiaModel[]>();
const grupos = ref<SelectBoxGuidEntityWithDefaultModel[]>([]);
const idGrupoSeleccionado = ref<string>();

const gruposSelect = computed<GrupoSelectItem[]>(() =>
  grupos.value.map((g) => ({
    ...g,
    Texto: formatGrupoTexto(g),
  })),
);

const criteriosOferta = computed(() => {
  return gridSource.value?.filter(item => item.TipoConcepto === TipoConceptoCriterioRetrocomisionEnergiaEnum.Oferta) || [];
});

const criteriosServicio = computed(() => {
  return gridSource.value?.filter(item => item.TipoConcepto === TipoConceptoCriterioRetrocomisionEnergiaEnum.Servicio) || [];
});

const modal = useModalService();
const gridProperties = ref<DxDataGrid>();

const props = defineProps({
  idEmpresaServicios: String
});

onMounted(async () => {
  gridProperties.value = getDefaultGridProperties();
  await cargarGrupos();
  await setDatasource();
});

function formatGrupoTexto(grupo: SelectBoxGuidEntityWithDefaultModel): string {
  debugger;
  const date = new Date(grupo.Denominacion);
  const fechaTexto = Number.isNaN(date.getTime())
    ? grupo.Denominacion
    : date.toLocaleDateString(intl.i18n.locale.value);
  const i18nKey = grupo.PorDefecto
    ? "retrocomisiones.grupo.desde_vigente"
    : "retrocomisiones.grupo.desde";
  return intl.i18n.t(i18nKey, { fecha: fechaTexto });
}

async function cargarGrupos() {
  const apiFunction = await api.toStandardApiFunction("getGruposCriteriosRetrocomisionEnergiaByIdEmpresa", { showLoading: false });
  grupos.value = await apiFunction(props.idEmpresaServicios!);
  idGrupoSeleccionado.value = resolveGrupoPorDefecto(grupos.value);
}

async function onGrupoChanged() {
  await setDatasource();
}

function calculateTarifaAcceso(rowData: any) {
  if (Array.isArray(rowData.TarifaAcceso) && rowData.TarifaAcceso.length > 0) {
    return rowData.TarifaAcceso.join(", ");
  }
  return "";
}

async function crearGrupo() {
  modal.showModal(
    GrupoCriterioRetrocomisionEnergiaForm,
    { idEmpresaServicios: props.idEmpresaServicios },
    { title: intl.i18n.t("retrocomisiones.grupo.creacion.titulo"), width: 480, height: "auto" }
  ).then(async (value) => {
    if (typeof value !== "string" || !value || value === messageBoxResult.cancel) {
      return;
    }
    await cargarGrupos();
    idGrupoSeleccionado.value = value;
    await setDatasource();
  });
}

async function crearRetroComision(tipoConcepto: TipoConceptoCriterioRetrocomisionEnergiaEnum) {
  if (!idGrupoSeleccionado.value)
    return;

  let title = `${intl.i18n.t("retrocomisiones.creacion.titulo")} 
    ${tipoConcepto === TipoConceptoCriterioRetrocomisionEnergiaEnum.Oferta
      ? intl.i18n.t("retrocomisiones.creacion.oferta")
      : intl.i18n.t("retrocomisiones.creacion.servicio")}`;

  modal.showModal(
    RetrocomisionEnergiaForm,
    {
      model: new CreateRetrocomisionEnergia({
        IdEmpresaServicios: props.idEmpresaServicios,
        IdGrupo: idGrupoSeleccionado.value,
        TipoConcepto: tipoConcepto
      }),
      isEditMode: false
    },
    { title: title, width: 500, height: 400 }
  ).then(async (value) => {
    if (value == messageBoxResult.ok)
      await setDatasource();
  });
}

const setDatasource = async () => {
  if (!idGrupoSeleccionado.value) {
    gridSource.value = [];
    return;
  }

  const apiFunction = await api.toStandardApiFunction("getCriteriosRetrocomisionEnergiaByIdEmpresa", { showLoading: true });
  gridSource.value = await apiFunction(props.idEmpresaServicios!, idGrupoSeleccionado.value);
};

const verDetalle = async (idRetroComision: string, tipoConcepto: TipoConceptoCriterioRetrocomisionEnergiaEnum) => {
  let title = `${intl.i18n.t("retrocomisiones.edicion.titulo")}
    ${tipoConcepto === TipoConceptoCriterioRetrocomisionEnergiaEnum.Oferta
      ? intl.i18n.t("retrocomisiones.creacion.oferta")
      : intl.i18n.t("retrocomisiones.creacion.servicio")}`;
  await api.callStandardApi("getCriterioRetrocomisionEnergiaById", { showLoading: true }, props.idEmpresaServicios!, idRetroComision)
    .then(value => {
      modal.showModal(
        RetrocomisionEnergiaForm,
        { model: new CreateRetrocomisionEnergia({ IdEmpresaServicios: props.idEmpresaServicios! }).createFromRetrocomisionDetail(value), isEditMode: true },
        { title: title, width: 500, height: "auto" }
      ).then(async (value) => {
        if (value == messageBoxResult.ok)
          await setDatasource();
      });
    });
};

const borrar = async (idRetroComision: string) => {
  let respuesta = await modal.messageBox(intl.i18n.t("retrocomisiones.grid.borrar_pregunta"), intl.i18n.t("retrocomisiones.grid.titulo_confirma_borrar"), "okCancel", "warning");
  if (respuesta == messageBoxResult.ok)
    await api.callStandardApi("deleteCriterioRetrocomisionEnergia", {}, props.idEmpresaServicios!, idRetroComision)
      .then(() => {
        toastSuccess(intl.i18n.t("retrocomisiones.borrado_exito"));
        gridSource.value = gridSource.value?.filter(item => item.Id !== idRetroComision);
      })
      .catch(() => toastError(intl.i18n.t("retrocomisiones.borrado_fallo")));
};

</script>

<style lang="scss" scoped>
</style>
